import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './TenantManagement.css'

const EMPTY_FORM = { name: '', slug: '', email_sender: '' }

const TenantManagement = () => {
  const { apiFetch } = useAuth()

  const [tenants, setTenants]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)
  const [success, setSuccess]     = useState(null)
  const [showForm, setShowForm]   = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm]           = useState(EMPTY_FORM)
  const [saving, setSaving]       = useState(false)
  const [togglingId, setTogglingId] = useState(null)

  const loadTenants = () => {
    setLoading(true)
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => setTenants(data))
      .catch(() => setError('Mandanten konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadTenants() }, [])

  const openCreateForm = () => {
    const wasEditing = editingId !== null
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowForm(wasEditing ? true : !showForm)
  }

  const openEditForm = (tenant) => {
    setEditingId(tenant.id)
    setForm({ name: tenant.name, slug: tenant.slug, email_sender: tenant.email_sender || '' })
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.slug.trim()) {
      setError('Name und Slug sind Pflichtfelder')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await apiFetch(
        editingId ? `/api/admin/tenants/${editingId}` : '/api/admin/tenants',
        { method: editingId ? 'PUT' : 'POST', body: JSON.stringify(form) }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setSuccess(editingId ? `Mandant "${form.name}" wurde aktualisiert` : `Mandant "${form.name}" wurde erstellt`)
      closeForm()
      loadTenants()
      setTimeout(() => setSuccess(null), 4000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (tenant) => {
    setTogglingId(tenant.id)
    setError(null)
    try {
      const res = await apiFetch(`/api/admin/tenants/${tenant.id}`, {
        method: 'PUT',
        body:   JSON.stringify({ active: !tenant.active }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setTenants(prev => prev.map(t => (t.id === tenant.id ? data : t)))
    } catch (err) {
      setError(err.message)
    } finally {
      setTogglingId(null)
    }
  }

  return (
    <div>
      <div className="page-header row-between">
        <div>
          <h1>Mandanten</h1>
          <p>Verwaltung aller Unternehmen im System</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateForm}>
          {showForm && !editingId ? 'Abbrechen' : '+ Neuer Mandant'}
        </button>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {showForm && (
        <div className="card tm-form-card">
          <div className="section-title">
            {editingId ? 'Mandant bearbeiten' : 'Neuen Mandanten erstellen'}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="tm-form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Acme GmbH"
                  required
                />
              </div>
              <div className="form-group">
                <label>Slug * (URL-Kürzel)</label>
                <input
                  className="form-control"
                  value={form.slug}
                  onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s/g, '-') }))}
                  placeholder="acme-gmbh"
                  required
                />
                <span className="tm-slug-hint">
                  Wird in der URL verwendet: /?tenant={form.slug || 'slug'}
                </span>
              </div>
            </div>
            <div className="form-group">
              <label>E-Mail Absender</label>
              <input
                className="form-control"
                type="email"
                value={form.email_sender}
                onChange={e => setForm(f => ({ ...f, email_sender: e.target.value }))}
                placeholder="checkin@acme-gmbh.de"
              />
            </div>
            <div className="tm-form-actions">
              <button type="button" className="btn btn-ghost" onClick={closeForm}>
                Abbrechen
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner spinner-sm" /> {editingId ? 'Aktualisieren...' : 'Erstellen...'}</>
                  : (editingId ? 'Aktualisieren' : 'Erstellen')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : tenants.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <p>Noch keine Mandanten vorhanden</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>E-Mail Absender</th>
                  <th>Status</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong></td>
                    <td>
                      <code className="tm-slug-code">{t.slug}</code>
                    </td>
                    <td className="tm-muted-cell">{t.email_sender || '—'}</td>
                    <td>
                      <span className={`badge ${t.active ? 'badge-green' : 'badge-grey'}`}>
                        {t.active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="tm-muted-cell">
                      {new Date(t.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="tm-actions-cell">
                      <button className="btn btn-ghost btn-sm" onClick={() => openEditForm(t)}>
                        Bearbeiten
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={togglingId === t.id}
                        onClick={() => handleToggleActive(t)}
                      >
                        {togglingId === t.id
                          ? <span className="spinner spinner-xs" />
                          : (t.active ? 'Deaktivieren' : 'Aktivieren')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Spans every tenant, not just one — this page has no single selected
          tenant, and restoring a tenants row already requires owner regardless. */}
      <VersionHistoryPanel table="tenants" global onRestored={loadTenants} />
    </div>
  )
}

export default TenantManagement
